import React, { useState } from 'react';
import axios from 'axios';
import { Package, MapPin, MessageCircle, Search, Loader2, CheckCircle2, ArrowUpRight, AlertCircle, Phone } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { useSiteSettings } from '../context/SiteSettingsContext';
import { europeBranches as defEU, sriLankaBranches as defSL, heroImages } from '../mock/data';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TrackParcel = () => {
  const { getContent } = useSiteSettings();
  const branchesEU = getContent('branches_eu', defEU);
  const branchesSL = getContent('branches_sl', defSL);
  const [f, setF] = useState({ parcel_number: '', branch_name: '', name: '', phone: '', note: '' });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [err, setErr] = useState('');
  const set = (k, v) => setF({ ...f, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    if (!f.parcel_number) { setErr('Please enter your parcel number.'); return; }
    if (!f.branch_name) { setErr('Please choose the branch handling your parcel.'); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/track`, f);
      const all = [...branchesEU, ...branchesSL];
      const branch = all.find((b) => b.name === f.branch_name);
      const wa = (branch?.whatsapp || branch?.phone || '').replace(/[^0-9]/g, '');
      const parts = [
        `📦 *Parcel tracking request*`,
        ``,
        `*Parcel #:* ${f.parcel_number}`,
        `*Branch:* ${branch?.name}`,
        f.name ? `*Name:* ${f.name}` : '',
        f.phone ? `*Phone:* ${f.phone}` : '',
        f.note ? `*Message:* ${f.note}` : '',
        ``,
        `Sent via lankaexpress.eu tracking form`,
      ].filter(Boolean).join('\n');
      const waUrl = wa ? `https://wa.me/${wa}?text=${encodeURIComponent(parts)}` : '';
      setDone({ branch, waUrl });
      if (waUrl) window.open(waUrl, '_blank');
    } catch (e2) {
      setErr(e2?.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally { setBusy(false); }
  };

  if (done) {
    return (
      <div className="min-h-[calc(100vh-200px)] flex items-center justify-center bg-neutral-50 py-16">
        <div className="max-w-lg w-full mx-6 bg-white p-10 border border-neutral-200 text-center">
          <div className="w-16 h-16 rounded-full bg-[#c8102e] flex items-center justify-center mx-auto mb-5"><CheckCircle2 className="w-7 h-7 text-white" /></div>
          <div className="flex items-center gap-3 justify-center mb-3"><span className="h-px w-10 bg-[#c8102e]" /><span className="eyebrow">Sent</span></div>
          <h1 className="font-serif-display text-3xl font-bold text-black mb-3">Your tracking request is with our team</h1>
          <p className="text-sm text-neutral-600 mb-6">Our employees at <span className="font-semibold text-black">{done.branch?.name}</span> will reply to you shortly. We've opened a WhatsApp chat with them for you.</p>
          {done.waUrl ? (
            <a href={done.waUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 bg-[#25D366] hover:brightness-110 text-white h-12 px-6 font-semibold"><MessageCircle className="w-4 h-4" /> Open WhatsApp chat</a>
          ) : (
            <p className="text-xs text-neutral-500">No WhatsApp number set for this branch. Our team will reach you soon.</p>
          )}
          <button onClick={() => { setDone(null); setF({ parcel_number: '', branch_name: '', name: '', phone: '', note: '' }); }} className="mt-6 block mx-auto text-sm text-neutral-500 hover:text-[#c8102e]">Track another parcel</button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <section className="relative h-[320px] flex items-center bg-black overflow-hidden">
        <img src={heroImages.warehouse} alt="Track" className="absolute inset-0 w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/70 to-transparent" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 w-full">
          <div className="flex items-center gap-3 mb-4"><span className="h-px w-10 bg-[#c8102e]" /><span className="eyebrow text-white">Tracking</span></div>
          <h1 className="text-white font-serif-display text-5xl md:text-6xl font-bold leading-[1.05]">Track your parcel</h1>
          <p className="text-white/75 mt-3 max-w-xl">Enter your parcel number and choose the branch handling it — we'll connect you directly with the employee in charge.</p>
        </div>
      </section>

      <section className="py-14 bg-neutral-50">
        <div className="max-w-2xl mx-auto px-6">
          <form onSubmit={submit} className="bg-white p-6 md:p-8 border border-neutral-200">
            <div className="mb-6">
              <label className="block text-[11px] tracking-[0.2em] uppercase text-neutral-600 font-semibold mb-2">Parcel number *</label>
              <div className="relative">
                <Package className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400" />
                <Input value={f.parcel_number} onChange={(e) => set('parcel_number', e.target.value)} placeholder="e.g. LEX-2025-004821" className="pl-11 h-12" />
              </div>
            </div>
            <div className="mb-6">
              <label className="block text-[11px] tracking-[0.2em] uppercase text-neutral-600 font-semibold mb-2">Branch handling your parcel *</label>
              <select value={f.branch_name} onChange={(e) => set('branch_name', e.target.value)} className="w-full h-12 border border-neutral-300 px-3 bg-white text-sm">
                <option value="">— Select branch —</option>
                <optgroup label="Europe">
                  {branchesEU.map((b) => <option key={b.name} value={b.name}>{b.name}{b.city ? ' — ' + b.city : ''}</option>)}
                </optgroup>
                <optgroup label="Sri Lanka">
                  {branchesSL.map((b) => <option key={b.name} value={b.name}>{b.name}</option>)}
                </optgroup>
              </select>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div>
                <label className="block text-[11px] tracking-[0.2em] uppercase text-neutral-600 font-semibold mb-2">Your name</label>
                <Input value={f.name} onChange={(e) => set('name', e.target.value)} className="h-12" />
              </div>
              <div>
                <label className="block text-[11px] tracking-[0.2em] uppercase text-neutral-600 font-semibold mb-2">Phone</label>
                <div className="relative">
                  <Phone className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400" />
                  <Input value={f.phone} onChange={(e) => set('phone', e.target.value)} className="pl-11 h-12" />
                </div>
              </div>
            </div>
            <div className="mb-6">
              <label className="block text-[11px] tracking-[0.2em] uppercase text-neutral-600 font-semibold mb-2">Additional info</label>
              <Textarea value={f.note} onChange={(e) => set('note', e.target.value)} rows={3} placeholder="Any details about your parcel that will help us find it faster..." />
            </div>
            {err && <div className="mb-4 flex items-center gap-2 text-sm text-[#c8102e] bg-red-50 border border-red-200 p-3"><AlertCircle className="w-4 h-4" /> {err}</div>}
            <Button type="submit" disabled={busy} className="w-full bg-[#c8102e] hover:bg-black text-white h-13 font-semibold tracking-[0.18em] uppercase text-[13px]">
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />} Request Tracking
            </Button>
            <p className="text-xs text-neutral-400 text-center mt-4">Our employees will reply to you soon via WhatsApp with your parcel status.</p>
          </form>
        </div>
      </section>
    </div>
  );
};

export default TrackParcel;
